/**
 * Unit-scope panels shared by the team boards (Team Access, Allocation):
 *
 *   UnitChooser     — the caller governs several units and none is selected
 *                     yet; one tap picks the unit the board (and, on a session
 *                     surface, the agent) is framed to.
 *   NoUnitAuthority — the caller governs no unit; an actionable dead-end
 *                     replacement (set your workplace, or — when the SERVER
 *                     refused a specific unit — whatever it said to do about
 *                     that unit, which is the sentence naming who can assign
 *                     her and that she may request access).
 *
 * Presentational only — the connected wrappers own resolution (useUnitScope)
 * and persistence (PUT /sessions/{id}/location on a session surface, local state
 * on a session-less one).
 */

import { useTranslation } from 'react-i18next'
import { ChevronRight, MapPin } from 'lucide-react'

import { cn } from '@/lib/utils'
import { EmptyState } from '@/components/clinical'
import { locationKindKey, resourceIcon } from './accessMeta'
import type { AccessRef } from '@/api/endpoints/access'

export function UnitChooser({
  options,
  onPick,
  pending,
  error,
}: {
  options: AccessRef[]
  onPick: (id: string) => void
  /** A pick is being persisted (session link in flight) — rows disabled. */
  pending?: boolean
  /** The last pick failed to persist; rows stay tappable to retry. */
  error?: boolean
}) {
  const { t } = useTranslation()
  const Icon = resourceIcon('node')
  return (
    <section className="mx-auto w-full max-w-xl space-y-4 p-4 pt-10">
      <header className="space-y-1 text-center">
        <h2 className="text-[19px] font-semibold tracking-[-0.01em] text-neutral-900">
          {t('access.unitScope.choose.title')}
        </h2>
        <p className="text-[14px] text-neutral-500">
          {t('access.unitScope.choose.description')}
        </p>
      </header>
      {error && (
        <p role="alert" className="text-center text-[13px] font-medium text-destructive">
          {t('access.unitScope.choose.error')}
        </p>
      )}
      <ul className="space-y-2">
        {options.map((unit) => (
          <li key={unit.id}>
            <button
              type="button"
              disabled={pending}
              onClick={() => onPick(unit.id)}
              className={cn(
                'flex min-h-12 w-full items-center gap-3 rounded-[14px] border border-neutral-200',
                'bg-white px-4 py-3 text-left transition active:scale-[0.98]',
                'hover:border-neutral-300 disabled:opacity-50',
              )}
            >
              <Icon className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-semibold text-neutral-900">
                  {unit.label && unit.label.trim() !== '' ? unit.label : unit.id}
                </span>
                {unit.kind && (
                  <span className="block truncate text-[13px] text-neutral-500">
                    {t(locationKindKey(unit.kind), unit.kind)}
                  </span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-neutral-300" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function NoUnitAuthority({
  onOpenProfile,
  deniedMessage,
  profileUnitLabel,
}: {
  onOpenProfile: () => void
  /**
   * The server's own refusal, when there was one (403 UNIT_LEAD_REQUIRED /
   * ACCESS_DENIED). It names the lead who can assign her and offers the access
   * request — strictly more actionable than the generic copy, and it used to
   * reach the console and nowhere else.
   */
  deniedMessage?: string
  /**
   * Her own profile unit, when she has one but doesn't lead it (deriveUnitScope
   * in useAccess.ts). The generic copy tells her to "set your workplace" —
   * misleading when she already has, so this names the unit she actually
   * works on instead. Only used when there's no sharper server refusal.
   */
  profileUnitLabel?: string
}) {
  const { t } = useTranslation()
  const description =
    deniedMessage ||
    (profileUnitLabel
      ? t('access.unitScope.noAuthority.descriptionWithUnit', { unit: profileUnitLabel })
      : t('access.unitScope.noAuthority.description'))
  return (
    <div className="grid h-full place-items-center">
      <EmptyState
        icon={<MapPin aria-hidden="true" />}
        title={t('access.unitScope.noAuthority.title')}
        description={description}
        action={{
          label: t('access.unitScope.noAuthority.setWorkplace'),
          onClick: onOpenProfile,
        }}
      />
    </div>
  )
}
